/**
 * AI Host Setup Wizard
 *
 * 3-step infrastructure setup (one-time only):
 * 1. Welcome - Introduction
 * 2. Phone Number - Provision Twilio number
 * 3. Stripe Connect - Link payment account
 *
 * Settings (greetings, hours, etc.) are managed separately in AIHostSettingsPage.
 *
 * Author: Dine AI
 * Created: November 24, 2025
 */

import React, { useState, useEffect } from 'react';
import { X, ArrowRight, ArrowLeft, Phone, CreditCard, CheckCircle, Sparkles, Clock, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { apiClient } from '@/services/api/apiClient';

interface AIHostSetupWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

type SetupStep = 1 | 2 | 3;

interface PhoneNumber {
  phone_number: string;
  friendly_name: string;
  locality?: string;
  region?: string;
  number_type?: string;
}

const AIHostSetupWizard: React.FC<AIHostSetupWizardProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState<SetupStep>(1);

  // Phone Number State
  const [areaCode, setAreaCode] = useState('');
  const [searchingNumbers, setSearchingNumbers] = useState(false);
  const [availableNumbers, setAvailableNumbers] = useState<PhoneNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<string>('');
  const [provisioningNumber, setProvisioningNumber] = useState(false);
  const [phoneAlreadyProvisioned, setPhoneAlreadyProvisioned] = useState(false);

  // Stripe Connect State
  const [stripeAccountStatus, setStripeAccountStatus] = useState<'not_started' | 'pending' | 'verified'>('not_started');
  const [checkingStripeStatus, setCheckingStripeStatus] = useState(false);

  /**
   * Check existing setup status on mount
   */
  useEffect(() => {
    if (isOpen) {
      checkExistingSetup();
    }
  }, [isOpen]);

  const checkExistingSetup = async () => {
    try {
      const response = await apiClient.get('/virtual-host/setup/status');
      const data = response.data;

      // If phone already configured, skip to Step 3 and mark as provisioned
      if (data.phone_configured && data.phone_number) {
        setSelectedNumber(data.phone_number);
        setPhoneAlreadyProvisioned(true);
        setCurrentStep(3);
        toast.info('Phone number already configured');
      }

      // Load Stripe status
      if (data.stripe_status) {
        setStripeAccountStatus(data.stripe_status);
      }
    } catch (error) {
      console.error('Error checking setup status:', error);
    }
  };

  /**
   * Step 2: Search for available phone numbers
   */
  const handleSearchNumbers = async () => {
    if (areaCode.length !== 3) {
      toast.error('Please enter a 3-digit area code');
      return;
    }

    try {
      setSearchingNumbers(true);
      const response = await apiClient.get(`/virtual-hostess/twilio/search-numbers?area_code=${areaCode}`);
      const numbers = response.data.numbers || [];

      setAvailableNumbers(numbers);

      if (numbers.length === 0) {
        toast.error('No numbers found. Try another area code (e.g., 512, 415, 646)');
      } else {
        toast.success(`Found ${numbers.length} available numbers`);
      }
    } catch (error) {
      console.error('Error searching numbers:', error);
      toast.error('Failed to search phone numbers');
    } finally {
      setSearchingNumbers(false);
    }
  };

  /**
   * Step 2: Provision selected phone number
   */
  const handleProvisionNumber = async () => {
    if (!selectedNumber) {
      toast.error('Please select a phone number');
      return;
    }

    try {
      setProvisioningNumber(true);
      await apiClient.post('/virtual-hostess/twilio/provision-number', {
        phone_number: selectedNumber
      });

      toast.success('Phone number provisioned successfully!');
      setCurrentStep(3); // Move to Stripe Connect
    } catch (error: any) {
      console.error('Error provisioning number:', error);
      toast.error(error.response?.data?.detail || 'Failed to provision phone number');
    } finally {
      setProvisioningNumber(false);
    }
  };

  /**
   * Step 3: Skip Stripe payment setup (pay at pickup only)
   */
  const handleSkipPayments = async () => {
    try {
      await apiClient.post('/virtual-host/setup/skip-payments');
      toast.success('Payment setup skipped. Customers will pay at pickup.');
      onComplete?.(); // Complete setup
    } catch (error: any) {
      console.error('Error skipping payments:', error);
      toast.error(error.response?.data?.detail || 'Failed to skip payment setup');
    }
  };

  /**
   * Step 3: Create Stripe Connected Account
   */
  const handleCreateStripeAccount = async () => {
    try {
      const response = await apiClient.post('/stripe-connect/create-account', {
        country: 'US'
      });

      if (response.data.onboarding_url) {
        toast.success('Redirecting to Stripe onboarding...');
        window.location.href = response.data.onboarding_url;
      }
    } catch (error: any) {
      console.error('Error creating Stripe account:', error);
      toast.error(error.response?.data?.detail || 'Failed to create Stripe account');
    }
  };

  /**
   * Step 3: Check Stripe account status
   */
  const handleCheckStripeStatus = async () => {
    try {
      setCheckingStripeStatus(true);
      const response = await apiClient.get('/stripe-connect/account-status');
      const data = response.data;

      if (data.charges_enabled && data.payouts_enabled) {
        setStripeAccountStatus('verified');
        toast.success('Stripe account verified!');

        // Setup complete!
        setTimeout(() => {
          if (onComplete) {
            onComplete();
          }
          onClose();
        }, 1500);
      } else if (data.details_submitted) {
        setStripeAccountStatus('pending');
        toast.info('Stripe account pending verification (1-2 days)');
      } else {
        toast.warning('Please complete Stripe onboarding');
      }
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      toast.error('Failed to check Stripe status');
    } finally {
      setCheckingStripeStatus(false);
    }
  };

  /**
   * Navigation handlers
   */
  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as SetupStep);
    }
  };

  const handleNextStep = async () => {
    // Step 2: Auto-provision phone number
    if (currentStep === 2 && selectedNumber) {
      await handleProvisionNumber();
      return;
    }

    // Step 3: Verify Stripe before completing
    if (currentStep === 3) {
      if (stripeAccountStatus === 'verified') {
        // Complete!
        if (onComplete) {
          onComplete();
        }
        onClose();
      } else {
        toast.error('Please complete Stripe verification first');
      }
      return;
    }

    // Default: Move to next step
    setCurrentStep((currentStep + 1) as SetupStep);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">AI Host Setup</h2>
            <p className="text-sm text-gray-600 mt-1">Quick infrastructure setup</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            {/* Step 1 */}
            <div className="flex flex-col items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all
                ${currentStep >= 1
                  ? 'bg-gradient-to-r from-[#FDB913] to-purple-500 text-white shadow-lg'
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {currentStep > 1 ? <CheckCircle className="w-6 h-6" /> : '1'}
              </div>
              <span className="text-xs text-gray-600 mt-2">Welcome</span>
            </div>

            {/* Line 1 → 2 */}
            <div className={`
              flex-1 h-1 mx-3 transition-all
              ${currentStep > 1 ? 'bg-gradient-to-r from-purple-500 to-[#FDB913]' : 'bg-gray-200'}
            `} />

            {/* Step 2 */}
            <div className="flex flex-col items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all
                ${currentStep >= 2
                  ? 'bg-gradient-to-r from-[#FDB913] to-purple-500 text-white shadow-lg'
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {currentStep > 2 ? <CheckCircle className="w-6 h-6" /> : '2'}
              </div>
              <span className="text-xs text-gray-600 mt-2">Phone</span>
            </div>

            {/* Line 2 → 3 */}
            <div className={`
              flex-1 h-1 mx-3 transition-all
              ${currentStep > 2 ? 'bg-gradient-to-r from-purple-500 to-[#FDB913]' : 'bg-gray-200'}
            `} />

            {/* Step 3 */}
            <div className="flex flex-col items-center">
              <div className={`
                flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all
                ${currentStep >= 3
                  ? 'bg-gradient-to-r from-[#FDB913] to-purple-500 text-white shadow-lg'
                  : 'bg-gray-200 text-gray-500'
                }
              `}>
                {stripeAccountStatus === 'verified' ? <CheckCircle className="w-6 h-6" /> : '3'}
              </div>
              <span className="text-xs text-gray-600 mt-2">Payment</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto flex-1">
          <AnimatePresence mode="wait">
            {/* Step 1: Welcome */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Welcome to AI Host!</h3>
                <p className="text-gray-600 mb-6">
                  Let's set up your AI-powered phone ordering system in just 3 quick steps.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                    <Phone className="w-6 h-6 text-orange-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Step 1: Phone Number</h4>
                      <p className="text-sm text-gray-600">Get a dedicated number for your restaurant (takes 30 seconds)</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                    <CreditCard className="w-6 h-6 text-purple-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">Step 2: Payment Setup</h4>
                      <p className="text-sm text-gray-600">Connect Stripe to receive payments (1-2 days verification)</p>
                    </div>
                  </div>

                  <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-sm text-blue-900">
                      💡 <strong>Pro Tip:</strong> While waiting for Stripe verification, you can configure all your settings
                      (greetings, hours, features) using the <strong>AI Host Settings</strong> button on your dashboard!
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Phone Number */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Provision Phone Number</h3>

                {/* Phone Already Provisioned - Read-Only View */}
                {phoneAlreadyProvisioned ? (
                  <div className="space-y-4">
                    <div className="p-6 bg-green-50 rounded-xl border-2 border-green-200">
                      <div className="flex items-center gap-3 mb-3">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div>
                          <h4 className="font-semibold text-gray-900">Phone Number Already Configured</h4>
                          <p className="text-sm text-gray-600">Your Twilio phone number is provisioned and ready</p>
                        </div>
                      </div>

                      <div className="mt-4 p-4 bg-white rounded-lg border border-green-200">
                        <div className="flex items-center gap-3">
                          <Phone className="w-5 h-5 text-gray-500" />
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Your Phone Number</p>
                            <p className="text-lg font-bold text-gray-900">{selectedNumber}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-900">
                        ✓ This phone number is already provisioned. Click <strong>Next</strong> to continue to payment setup.
                      </p>
                    </div>
                  </div>
                ) : (
                  /* New Phone Provisioning - Editable View */
                  <>
                    <p className="text-gray-600 mb-6">
                      Search for available phone numbers by area code.
                    </p>

                    {/* Search */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Area Code
                      </label>
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={areaCode}
                          onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                          placeholder="Enter 3-digit area code (e.g., 512)"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          maxLength={3}
                        />
                        <button
                          onClick={handleSearchNumbers}
                          disabled={searchingNumbers || areaCode.length !== 3}
                          className="px-6 py-3 bg-gradient-to-r from-[#FDB913] to-purple-500 hover:from-[#e5a511] hover:to-purple-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {searchingNumbers ? 'Searching...' : 'Search'}
                        </button>
                      </div>
                    </div>

                    {/* Available Numbers */}
                    {availableNumbers.length > 0 && (
                      <div className="space-y-3">
                        <h4 className="font-semibold text-gray-900">Available Numbers:</h4>
                        {availableNumbers.map((number) => (
                          <div
                            key={number.phone_number}
                            onClick={() => setSelectedNumber(number.phone_number)}
                            className={`
                              p-4 border-2 rounded-lg cursor-pointer transition-all
                              ${selectedNumber === number.phone_number
                                ? 'border-purple-600 bg-purple-50'
                                : 'border-gray-200 hover:border-purple-300'
                              }
                            `}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-semibold text-gray-900">{number.phone_number}</p>
                                  {number.number_type === 'toll_free' && (
                                    <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-semibold rounded-full">
                                      Toll-Free
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600">{number.locality || 'Nationwide'}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </motion.div>
            )}

            {/* Step 3: Stripe Connect */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Payment Setup</h3>
                <p className="text-gray-600 mb-6">
                  Connect your Stripe account to receive payments directly from customers.
                </p>

                {stripeAccountStatus === 'not_started' && (
                  <div className="space-y-4">
                    <div className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                      <h4 className="font-semibold text-gray-900 mb-2">Why Stripe?</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>✓ Secure payment processing</li>
                        <li>✓ Direct deposits to your bank account</li>
                        <li>✓ Industry-leading fraud protection</li>
                        <li>✓ Instant payment confirmation</li>
                      </ul>
                    </div>

                    <button
                      onClick={handleCreateStripeAccount}
                      className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold transition-all shadow-lg"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        <span>Connect Stripe Account</span>
                      </div>
                    </button>

                    {/* Skip for now option */}
                    <div className="relative my-4">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">or</span>
                      </div>
                    </div>

                    <button
                      onClick={handleSkipPayments}
                      className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                    >
                      <div className="flex items-center justify-center gap-2">
                        <span>Skip for now (pay at pickup only)</span>
                      </div>
                    </button>

                    <p className="text-xs text-gray-500 text-center mt-2">
                      You can connect Stripe later in AI Host Settings.
                    </p>
                  </div>
                )}

                {(stripeAccountStatus === 'pending' || stripeAccountStatus === 'verified') && (
                  <div className="space-y-4">
                    <div className={`p-6 rounded-xl border ${
                      stripeAccountStatus === 'verified'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center gap-3 mb-3">
                        {stripeAccountStatus === 'verified' ? (
                          <CheckCircle className="w-8 h-8 text-green-600" />
                        ) : (
                          <Clock className="w-8 h-8 text-yellow-600 animate-pulse" />
                        )}
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {stripeAccountStatus === 'verified' ? 'Account Verified!' : 'Verification Pending'}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {stripeAccountStatus === 'verified'
                              ? 'Your Stripe account is ready to accept payments'
                              : 'Stripe is reviewing your account (typically 1-2 business days)'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={handleCheckStripeStatus}
                      disabled={checkingStripeStatus}
                      className="w-full px-6 py-3 bg-white border-2 border-purple-500 text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors disabled:opacity-50"
                    >
                      {checkingStripeStatus ? 'Checking...' : 'Check Status'}
                    </button>

                    {stripeAccountStatus === 'pending' && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm text-blue-900">
                          💡 <strong>What's next?</strong> While waiting for verification, configure your AI Host settings
                          (greetings, hours, features) so you're ready to go live immediately when approved!
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handlePrevStep}
            disabled={currentStep === 1 || (currentStep === 3 && phoneAlreadyProvisioned)}
            className="flex items-center gap-2 px-6 py-3 text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Previous</span>
          </button>

          <div className="text-sm text-gray-600">
            Step {currentStep} of 3
          </div>

          <button
            onClick={handleNextStep}
            disabled={
              (currentStep === 2 && !selectedNumber) ||
              (currentStep === 3 && stripeAccountStatus !== 'verified')
            }
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FDB913] to-purple-500 hover:from-[#e5a511] hover:to-purple-600 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
          >
            <span className="font-medium">
              {currentStep === 3 ? (stripeAccountStatus === 'verified' ? 'Complete' : 'Finish Later') : 'Next'}
            </span>
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AIHostSetupWizard;
